// TOUR-2 — DevPanel surface to exercise target highlights on known buttons.

import { useState } from "react";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { FIREFLY_TARGET_REGISTRY } from "@/lib/firefly/fireflyTargetRegistry";
import { collectProductionRegistryTargets } from "@/lib/firefly/fireflyTargetIntegrity";
import { FireflyTargetHighlightHost } from "@/components/activity/firefly/FireflyTargetHighlightHost";
import type { FireflyIntent } from "@/components/activity/firefly/fireflyIntents";

export function DevPanelFireflyTargetHighlightSection() {
  const [collapsed, setCollapsed] = useState(true);
  const [activeTargetId, setActiveTargetId] = useState<string | null>(null);
  const [intent, setIntent] = useState<Extract<FireflyIntent, "teaching" | "guidance">>("teaching");
  const productionTargets = collectProductionRegistryTargets();

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900/45">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
            Firefly target highlight (TOUR-2)
          </h3>
          <p className="mt-1 text-xs text-gray-600 dark:text-zinc-300">
            Pulse <span className="font-mono">{FIREFLY_TARGET_ATTR}</span> anchors without
            mutations. Reduced motion → outline only.
          </p>
        </div>
        <span className="shrink-0 text-lg text-gray-500 dark:text-zinc-400" aria-hidden>
          {collapsed ? "\u25b8" : "\u25be"}
        </span>
      </button>

      {!collapsed ? (
        <div className="mt-3 space-y-3 border-t border-gray-200 pt-3 dark:border-zinc-700">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-gray-600 dark:text-zinc-300">
              Intent
              <select
                className="ml-2 rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-zinc-600 dark:bg-zinc-900"
                value={intent}
                onChange={(e) =>
                  setIntent(e.target.value as Extract<FireflyIntent, "teaching" | "guidance">)
                }
              >
                <option value="teaching">teaching</option>
                <option value="guidance">guidance</option>
              </select>
            </label>
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-2 py-1 text-xs dark:border-zinc-600"
              onClick={() => setActiveTargetId(null)}
            >
              Clear highlight
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              {...{ [FIREFLY_TARGET_ATTR]: "dev-highlight-demo-a" }}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white"
              onClick={() => setActiveTargetId("dev-highlight-demo-a")}
            >
              Demo A (highlight self)
            </button>
            <button
              type="button"
              {...{ [FIREFLY_TARGET_ATTR]: "dev-highlight-demo-b" }}
              className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white"
              onClick={() => setActiveTargetId("dev-highlight-demo-b")}
            >
              Demo B (highlight self)
            </button>
          </div>

          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600 dark:text-zinc-400">
              Production targets ({productionTargets.length})
            </div>
            <ul className="max-h-48 space-y-1 overflow-y-auto">
              {productionTargets.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate font-mono text-gray-800 dark:text-zinc-200" title={t.label}>
                    {t.id}
                  </span>
                  <button
                    type="button"
                    className="shrink-0 rounded border border-gray-300 px-2 py-0.5 text-[11px] dark:border-zinc-600"
                    onClick={() => setActiveTargetId(t.id)}
                  >
                    Highlight
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-gray-500 dark:text-zinc-400">
              Open the target mode before highlighting (e.g. Leads for send-lead). Conditional
              targets (toast, stale-WAC, intake row buttons) only resolve when their signal is
              active. Registry: {FIREFLY_TARGET_REGISTRY.length} total incl. 2 dev demos below.
            </p>
          </div>
        </div>
      ) : null}

      <FireflyTargetHighlightHost
        targetId={activeTargetId}
        intent={intent}
        onDismiss={() => setActiveTargetId(null)}
      />
    </section>
  );
}
